import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Reorder, useDragControls } from "framer-motion";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { ProgressBar, ProgressRing } from "@/components/common/Progress";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, PRIORITY_STATUS, PLAN_STATUS, ACTION_STATUS, RAG, BUSINESS_AREAS, LEVEL } from "@/lib/format";
import { toast } from "sonner";

const PRIORITY_STATUSES = ["not_started", "in_progress", "on_track", "at_risk", "blocked", "completed"];
const PLAN_STATUSES = ["draft", "active", "at_risk", "completed", "archived"];
const RISK = ["low", "medium", "high"];
const KANBAN_COLS = [
  { key: "not_started", label: "Not started" },
  { key: "in_progress", label: "In progress" },
  { key: "on_track", label: "On track" },
  { key: "at_risk", label: "At risk / Blocked", match: ["at_risk", "blocked"] },
  { key: "completed", label: "Completed" },
];

export default function PlanDetail() {
  const { id } = useParams();
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);

  const load = () => api.get(`/plans/${id}`).then((r) => setPlan(r.data)).catch(() => navigate("/plan"));
  useEffect(() => { setPlan(null); load(); if (orgId) api.get(`/organisations/${orgId}/members`).then((r) => setMembers(r.data)); }, [id, orgId]);
  useEffect(() => { setItems(plan?.priorities || []); }, [plan]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const saveOrder = async () => {
    try {
      await api.post(`/plans/${id}/priorities/reorder`, { priority_ids: itemsRef.current.map((p) => p.id) });
      await load();
    } catch (e) { toast.error("Couldn't save the new order"); }
  };

  const updateStatus = async (s) => {
    const prev = plan.status;
    setPlan((pl) => ({ ...pl, status: s }));
    try {
      await api.patch(`/plans/${id}`, { status: s });
      toast.success(`Plan marked ${PLAN_STATUS[s]?.label || s}`);
    } catch (e) {
      setPlan((pl) => ({ ...pl, status: prev }));
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!plan) return <PageLoading />;

  const priorities = plan.priorities || [];

  const priorityBody = (p) => (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <RagBadge rag={PRIORITY_STATUS[p.status]?.rag}>{PRIORITY_STATUS[p.status]?.label}</RagBadge>
          {p.risk_level === "high" && <RagBadge rag="red">High risk</RagBadge>}
          <span className="text-xs text-[#1E2D44]/50">{p.business_area}</span>
        </div>
        <h3 className="mt-2 font-display text-lg font-bold text-[#0B1729]">{p.title}</h3>
        <p className="mt-1 text-sm text-[#1E2D44]/60 line-clamp-2">{p.description}</p>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[#1E2D44]/55">
          <span className="flex items-center gap-1"><Icons.User size={13} /> {p.owner_name || "Unassigned"}</span>
          <span className="flex items-center gap-1"><Icons.Calendar size={13} /> Due {fmtDate(p.due_date)}</span>
          <span className="flex items-center gap-1"><Icons.ListChecks size={13} /> {(p.actions || []).length} actions</span>
        </div>
      </div>
      <div className="w-full lg:w-48">
        <div className="mb-1.5 flex justify-between text-xs"><span className="text-[#1E2D44]/50">Progress</span><span className="font-semibold">{p.percent_complete}%</span></div>
        <ProgressBar value={p.percent_complete} color={RAG[PRIORITY_STATUS[p.status]?.rag || "grey"].solid} />
      </div>
    </div>
  );

  return (
    <div data-testid="plan-detail-page">
      <button onClick={() => navigate("/plan")} className="mb-4 flex items-center gap-1.5 text-sm text-[#1E2D44]/55 hover:text-[#FFB000]">
        <Icons.ArrowLeft size={15} /> All plans
      </button>

      {/* Header */}
      <div className="amden-card mb-6 p-6 lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <ProgressRing value={plan.percent} size={92} stroke={9} />
            <div>
              <div className="flex items-center gap-2">
                {canEdit ? (
                  <Select value={plan.status} onValueChange={updateStatus}>
                    <SelectTrigger className="h-8 w-[160px] border-[#E4E8EF]" data-testid="plan-status-select">
                      <span className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${RAG[PLAN_STATUS[plan.status]?.rag || "grey"].dot}`} />
                        <SelectValue />
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {PLAN_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`plan-status-option-${s}`}>{PLAN_STATUS[s]?.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <RagBadge rag={PLAN_STATUS[plan.status]?.rag}>{PLAN_STATUS[plan.status]?.label}</RagBadge>
                )}
                <span className="text-xs text-[#1E2D44]/50">{fmtDate(plan.start_date)} – {fmtDate(plan.end_date)}</span>
              </div>
              <h1 className="mt-1.5 font-display text-2xl font-bold text-[#0B1729]">{plan.title}</h1>
              <p className="mt-1 max-w-2xl text-sm text-[#1E2D44]/60">{plan.objective}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="border-[#E4E8EF]" data-testid="print-plan-btn">
              <Icons.Printer size={16} className="mr-1.5" /> Print
            </Button>
            {canEdit && (
              <Button onClick={() => setAddOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="add-priority-btn">
                <Icons.Plus size={16} className="mr-1.5" /> Priority
              </Button>
            )}
          </div>
        </div>
        {plan.success_definition && (
          <div className="mt-5 rounded-xl bg-[#F4F6F9] p-4">
            <div className="label-eyebrow mb-1">Definition of success</div>
            <p className="text-sm text-[#1E2D44]/75">{plan.success_definition}</p>
          </div>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-5 bg-[#E4E8EF]">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="tab-kanban">Kanban</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          {items.length === 0 ? (
            <EmptyState icon="ListTodo" title="No priorities yet" description="Add three to five strategic priorities for this cycle."
              action={canEdit && <Button onClick={() => setAddOpen(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">Add priority</Button>} />
          ) : canEdit ? (
            <>
              <p className="mb-3 flex items-center gap-1.5 text-xs text-[#1E2D44]/45"><Icons.GripVertical size={13} /> Drag the handle to reorder priorities</p>
              <Reorder.Group axis="y" values={items} onReorder={setItems} className="space-y-4">
                {items.map((p) => (
                  <PriorityReorderItem key={p.id} p={p} onOpen={() => setSelected(p)} onDrop={saveOrder}>
                    {priorityBody(p)}
                  </PriorityReorderItem>
                ))}
              </Reorder.Group>
            </>
          ) : (
            <div className="space-y-4">
              {items.map((p) => (
                <div key={p.id} className="amden-card amden-card-hover cursor-pointer p-6" onClick={() => setSelected(p)} data-testid={`priority-${p.id}`}>
                  {priorityBody(p)}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Kanban */}
        <TabsContent value="kanban">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {KANBAN_COLS.map((col) => {
              const items = priorities.filter((p) => (col.match || [col.key]).includes(p.status));
              return (
                <div key={col.key} className="rounded-xl bg-[#E4E8EF]/50 p-3">
                  <div className="mb-3 flex items-center justify-between px-1">
                    <span className="text-xs font-semibold text-[#1E2D44]/70">{col.label}</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-xs text-[#1E2D44]/50">{items.length}</span>
                  </div>
                  <div className="space-y-2.5">
                    {items.map((p) => (
                      <div key={p.id} onClick={() => setSelected(p)} className="cursor-pointer rounded-lg border border-[#E4E8EF] bg-white p-3 transition-colors hover:border-[#FFC24D]">
                        <div className="text-sm font-medium text-[#0B1729] line-clamp-2">{p.title}</div>
                        <div className="mt-2"><ProgressBar value={p.percent_complete} /></div>
                        <div className="mt-2 flex items-center justify-between text-[11px] text-[#1E2D44]/50">
                          <span>{p.owner_name}</span><span>{p.percent_complete}%</span>
                        </div>
                      </div>
                    ))}
                    {items.length === 0 && <div className="px-1 py-6 text-center text-xs text-[#1E2D44]/35">—</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline">
          <div className="amden-card p-6">
            <div className="space-y-4">
              {priorities.map((p) => {
                const start = plan.start_date, end = plan.end_date;
                const total = Math.max(1, new Date(end) - new Date(start));
                const left = Math.max(0, Math.min(95, ((new Date(p.start_date || start) - new Date(start)) / total) * 100));
                const width = Math.max(6, Math.min(100 - left, ((new Date(p.due_date || end) - new Date(p.start_date || start)) / total) * 100));
                return (
                  <div key={p.id} onClick={() => setSelected(p)} className="cursor-pointer">
                    <div className="mb-1.5 flex items-center justify-between text-sm">
                      <span className="font-medium text-[#0B1729]">{p.title}</span>
                      <span className="text-xs text-[#1E2D44]/50">{fmtDate(p.due_date)}</span>
                    </div>
                    <div className="relative h-8 rounded-lg bg-[#E4E8EF]">
                      <div className="absolute top-0 flex h-8 items-center rounded-lg px-3 text-xs font-medium text-white" style={{ left: `${left}%`, width: `${width}%`, backgroundColor: RAG[PRIORITY_STATUS[p.status]?.rag || "grey"].solid }}>
                        {p.percent_complete}%
                      </div>
                    </div>
                  </div>
                );
              })}
              {priorities.length === 0 && <EmptyState icon="GanttChart" title="No priorities" description="Add priorities to see the timeline." />}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {selected && (
        <PriorityDialog
          priority={selected}
          members={members}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
      {addOpen && (
        <AddPriorityDialog planId={id} orgId={orgId} members={members} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />
      )}
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="label-eyebrow mb-1">{label}</div>
      <p className="text-sm text-[#1E2D44]/80">{value}</p>
    </div>
  );
}

function PriorityReorderItem({ p, onOpen, onDrop, children }) {
  const controls = useDragControls();
  return (
    <Reorder.Item value={p} dragListener={false} dragControls={controls} onDragEnd={onDrop}
      className="amden-card p-6" data-testid={`priority-${p.id}`}>
      <div className="flex items-start gap-3">
        <button type="button"
          onPointerDown={(e) => { e.preventDefault(); controls.start(e); }}
          className="mt-1 shrink-0 cursor-grab touch-none text-[#1E2D44]/30 transition-colors hover:text-[#1E2D44]/60 active:cursor-grabbing"
          data-testid={`drag-priority-${p.id}`} aria-label="Drag to reorder">
          <Icons.GripVertical size={18} />
        </button>
        <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen}>
          {children}
        </div>
      </div>
    </Reorder.Item>
  );
}

function PriorityDialog({ priority, members, canEdit, onClose, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    title: priority.title || "",
    business_area: priority.business_area || BUSINESS_AREAS[0],
    description: priority.description || "",
    why_it_matters: priority.why_it_matters || "",
    desired_outcome: priority.desired_outcome || "",
    success_measure: priority.success_measure || "",
    owner_id: priority.owner_id || "",
    due_date: (priority.due_date || "").slice(0, 10),
  });
  const [pct, setPct] = useState(priority.percent_complete);
  const [status, setStatus] = useState(priority.status);
  const [risk, setRisk] = useState(priority.risk_level);
  const [commentary, setCommentary] = useState(priority.client_commentary || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ownerName = members.find((m) => m.id === form.owner_id)?.name || priority.owner_name;

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    setSaving(true);
    try {
      await api.patch(`/priorities/${priority.id}`, {
        ...form, due_date: form.due_date || null,
        percent_complete: pct, status, risk_level: risk, client_commentary: commentary,
      });
      toast.success("Priority updated");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this priority? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(`/priorities/${priority.id}`);
      toast.success("Priority deleted");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <RagBadge rag={PRIORITY_STATUS[status]?.rag}>{PRIORITY_STATUS[status]?.label}</RagBadge>
              <span className="text-xs text-[#1E2D44]/50">{form.business_area}</span>
            </div>
            {canEdit && !edit && (
              <Button variant="outline" onClick={() => setEdit(true)} className="h-8 border-[#E4E8EF] px-3 text-xs" data-testid="edit-priority-btn">
                <Icons.Pencil size={13} className="mr-1.5" /> Edit details
              </Button>
            )}
          </div>
          {edit ? (
            <>
              <DialogTitle className="sr-only">Edit priority</DialogTitle>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 h-11 font-display text-lg font-bold" data-testid="edit-priority-title" />
            </>
          ) : (
            <DialogTitle className="font-display text-xl">{form.title}</DialogTitle>
          )}
          <DialogDescription className="sr-only">Priority details, progress and related actions.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {edit ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div><Label className="text-xs">Business area</Label>
                  <Select value={form.business_area} onValueChange={(v) => set("business_area", v)}>
                    <SelectTrigger className="mt-1" data-testid="edit-priority-area"><SelectValue /></SelectTrigger>
                    <SelectContent>{BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Owner</Label>
                  <Select value={form.owner_id} onValueChange={(v) => set("owner_id", v)}>
                    <SelectTrigger className="mt-1" data-testid="edit-priority-owner"><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Due date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="mt-1" data-testid="edit-priority-due" />
                </div>
              </div>
              <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1" data-testid="edit-priority-description" /></div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><Label className="text-xs">Why it matters</Label><Textarea value={form.why_it_matters} onChange={(e) => set("why_it_matters", e.target.value)} className="mt-1" /></div>
                <div><Label className="text-xs">Desired outcome</Label><Textarea value={form.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)} className="mt-1" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Success measure</Label><Textarea value={form.success_measure} onChange={(e) => set("success_measure", e.target.value)} className="mt-1" /></div>
              </div>
            </div>
          ) : (
            <>
              <Field label="Description" value={form.description} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Why it matters" value={form.why_it_matters} />
                <Field label="Desired outcome" value={form.desired_outcome} />
                <Field label="Success measure" value={form.success_measure} />
                <Field label="Owner" value={ownerName} />
              </div>
            </>
          )}
          {priority.consultant_recommendation && (
            <div className="rounded-xl bg-[#0B1729] p-4 text-white">
              <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-[#FFC24D]"><Icons.Sparkles size={13} /> Consultant recommendation</div>
              <p className="text-sm text-white/80">{priority.consultant_recommendation}</p>
            </div>
          )}

          {(priority.actions || []).length > 0 && (
            <div>
              <div className="label-eyebrow mb-2">Related actions</div>
              <div className="space-y-2">
                {priority.actions.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-lg border border-[#EEF1F6] p-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`h-2 w-2 rounded-full ${RAG[ACTION_STATUS[a.status]?.rag || "grey"].dot}`} />
                      <span className="text-sm text-[#0B1729]">{a.title}</span>
                    </div>
                    <span className="text-xs text-[#1E2D44]/50">{fmtDate(a.due_date)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="rounded-xl border border-[#E4E8EF] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold">Update progress</span>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex justify-between text-sm"><span className="text-[#1E2D44]/60">Completion</span><span className="font-semibold">{pct}%</span></div>
                  <Slider value={[pct]} onValueChange={(v) => setPct(v[0])} max={100} step={5} data-testid="priority-progress-slider" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Status</Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="mt-1" data-testid="priority-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITY_STATUSES.map((s) => <SelectItem key={s} value={s}>{PRIORITY_STATUS[s].label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Risk level</Label>
                    <Select value={risk} onValueChange={setRisk}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{RISK.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Client commentary</Label>
                  <Textarea value={commentary} onChange={(e) => setCommentary(e.target.value)} className="mt-1" placeholder="Add an update…" data-testid="priority-commentary" />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          {canEdit ? (
            <Button variant="ghost" onClick={remove} disabled={deleting} className="text-[#C95555] hover:bg-[#C95555]/10 hover:text-[#C95555]" data-testid="delete-priority-btn">
              {deleting ? <Icons.Loader2 className="animate-spin" size={16} /> : <><Icons.Trash2 size={15} className="mr-1.5" /> Delete</>}
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            {canEdit && <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-priority-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Save"}</Button>}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPriorityDialog({ planId, orgId, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    plan_id: planId, title: "", business_area: BUSINESS_AREAS[0], description: "",
    why_it_matters: "", desired_outcome: "", success_measure: "", owner_id: "",
    due_date: "", status: "not_started", risk_level: "low",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try {
      await api.post(`/priorities?org_id=${orgId}`, form);
      toast.success("Priority added");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader><DialogTitle>Add strategic priority</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="new-priority-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Business area</Label>
              <Select value={form.business_area} onValueChange={(v) => setForm({ ...form, business_area: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{BUSINESS_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Owner</Label>
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Assign" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
          <div><Label>Desired outcome</Label><Input value={form.desired_outcome} onChange={(e) => setForm({ ...form, desired_outcome: e.target.value })} className="mt-1" /></div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-new-priority">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Add priority"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
